import type { LifeState } from './life.ts';
import { jumboOrder } from './style.ts';

/**
 * Your room, made yours: prints of real paintings (public domain: the Art Institute of Chicago, The Met), posters
 * (Paris in the 1890s, NASA's travel posters for other worlds, and the game's own: made-up bands and films), records,
 * shelves, plants, vines, lights. From Jumbo, delivered like anything else; once it's here it's up (the game finds
 * it a wall or a corner), and you can take any of it down again. What's up is part of your home's style, so anyone
 * who visits sees it too.
 */

export type DecorKind = 'painting' | 'poster' | 'records' | 'shelf' | 'vine' | 'hanger' | 'lights' | 'led' | 'lamp' | 'plant' | 'bookcase' | 'turntable';
export interface DecorItem {
  id: string;
  kind: DecorKind;
  label: string;
  /** a print or poster: who made it, when */
  artist?: string;
  year?: string;
  /** the picture's width over its height (a print or poster) */
  aspect?: number;
  /** which model (a plant) */
  model?: string;
  price: number;
  comfort: number;
  note: string;
}

const PRINT = 65_000, POSTER = 12_000;
// [id, title, artist, year, aspect]
const PAINTINGS: [string, string, string, string, number][] = [
  ['grandejatte', 'A Sunday on La Grande Jatte', 'Georges Seurat', '1884', 1.492],
  ['bedroom', 'The Bedroom', 'Vincent van Gogh', '1889', 1.277],
  ['rainyday', 'Paris Street; Rainy Day', 'Gustave Caillebotte', '1877', 1.287],
  ['waterlilies', 'Water Lilies', 'Claude Monet', '1906', 1.042],
  ['wheatstacks', 'Stacks of Wheat', 'Claude Monet', '1890', 1.693],
  ['childsbath', 'The Child’s Bath', 'Mary Cassatt', '1893', 0.659],
  ['vangoghself', 'Self-Portrait', 'Vincent van Gogh', '1887', 0.785],
  ['greatwave', 'The Great Wave off Kanagawa', 'Katsushika Hokusai', '1831', 1.463],
  ['cypresses', 'Wheat Field with Cypresses', 'Vincent van Gogh', '1889', 1.256],
  ['irises', 'Irises', 'Vincent van Gogh', '1890', 1.261],
  ['harvesters', 'The Harvesters', 'Pieter Bruegel the Elder', '1565', 1.357],
  ['waterpitcher', 'Young Woman with a Water Pitcher', 'Johannes Vermeer', '1662', 0.889],
  ['primavesi', 'Mäda Primavesi', 'Gustav Klimt', '1912', 0.742],
  ['shinohashi', 'Sudden Shower over Shin-Ōhashi Bridge', 'Utagawa Hiroshige', '1857', 0.685],
  ['cherryblossom', 'Swallows and Cherry Blossoms', 'Utagawa Hiroshige', '1830s', 0.356],
  ['tannerdisciples', 'The Two Disciples at the Tomb', 'Henry Ossawa Tanner', '1906', 0.818],
  ['manetbar', 'The Races at Longchamp', 'Édouard Manet', '1866', 1.92],
];
const POSTERS: [string, string, string, string, number][] = [
  ['p-moulinrouge', 'Moulin Rouge, La Goulue', 'Henri de Toulouse-Lautrec', '1891', 0.611],
  ['p-divan', 'Divan Japonais', 'Henri de Toulouse-Lautrec', '1893', 0.751],
  ['p-jane', 'Jane Avril', 'Henri de Toulouse-Lautrec', '1899', 0.678],
  ['p-bruant', 'Aristide Bruant', 'Henri de Toulouse-Lautrec', '1893', 0.755],
  ['p-reine', 'Reine de Joie', 'Henri de Toulouse-Lautrec', '1892', 0.682],
  ['p-zodiaque', 'Zodiaque', 'Alphonse Mucha', '1896', 0.745],
  ['p-mars', 'Mars: Visions of the Future', 'NASA/JPL', '2016', 0.667],
  ['p-jupiter', 'Jupiter: Visions of the Future', 'NASA/JPL', '2016', 0.667],
  ['p-grandtour', 'The Grand Tour: Visions of the Future', 'NASA/JPL', '2016', 0.667],
  ['p-enceladus', 'Enceladus: Visions of the Future', 'NASA/JPL', '2016', 0.667],
  ['p-ceres', 'Ceres: Visions of the Future', 'NASA/JPL', '2016', 0.667],
  ['p-kepler16b', 'Kepler-16b: Visions of the Future', 'NASA/JPL', '2016', 0.692],
  ['p-kepler186f', 'Kepler-186f: Visions of the Future', 'NASA/JPL', '2016', 0.695],
  ['p-cancri', '55 Cancri e: Visions of the Future', 'NASA/JPL', '2016', 0.694],
  ['p-pegasi', '51 Pegasi b: Visions of the Future', 'NASA/JPL', '2016', 0.671],
  ['p-hd40307g', 'HD 40307 g: Visions of the Future', 'NASA/JPL', '2016', 0.694],
  ['p-psoj318', 'PSO J318.5-22: Visions of the Future', 'NASA/JPL', '2016', 0.692],
  ['o-kayode', 'Kayode Sun: Live in Lagos', 'gig poster', '', 0.711],
  ['o-neondrift', 'Neon Drift', 'synthwave', '', 0.711],
  ['o-midnighttrio', 'The Midnight Trio', 'jazz', '', 0.711],
  ['o-lastdanfo', 'The Last Danfo', 'film poster', '', 0.711],
  ['o-skyblade', 'Sky Blade', 'anime', '', 0.711],
  ['o-hustle', 'Hustle', 'type poster', '', 0.711],
  ['o-lagoon', 'Visit Lagos Lagoon', 'travel poster', '', 0.711],
  ['o-goodvibes', 'Good Vibes Only', '70s print', '', 0.711],
  ['o-lofi', 'Rain Beats to Study To', 'lo-fi', '', 0.711],
];

export const DECOR: DecorItem[] = [
  ...PAINTINGS.map(([id, label, artist, year, aspect]): DecorItem => ({ id, kind: 'painting', label, artist, year, aspect, price: PRINT, comfort: 2, note: `${artist}, ${year}. A museum-quality print, framed.` })),
  ...POSTERS.map(([id, label, artist, year, aspect]): DecorItem => ({ id, kind: 'poster', label, artist, year, aspect, price: POSTER, comfort: 1, note: year ? `${artist}, ${year}. A poster in a thin black frame.` : `A poster (${artist}) in a thin black frame.` })),
  { id: 'recordwall', kind: 'records', label: 'Record wall', price: 55_000, comfort: 2, note: 'Six albums framed on the wall, the sleeves facing out.' },
  { id: 'turntable', kind: 'turntable', label: 'Record player and cabinet', price: 260_000, comfort: 3, note: 'A turntable on a low wooden cabinet, a crate of records beside it.' },
  { id: 'shelves', kind: 'shelf', label: 'Floating shelves', price: 40_000, comfort: 2, note: 'Two oak shelves on the wall: books, a little succulent, a candle.' },
  { id: 'bookcase', kind: 'bookcase', label: 'Bookcase, filled', price: 180_000, comfort: 3, note: 'A tall worn-wood bookcase, full of books.' },
  { id: 'plant-tall', kind: 'plant', model: 'potted_plant_01', label: 'Tall plant in a basket', price: 45_000, comfort: 2, note: 'Taller than the bed, in a woven basket.' },
  { id: 'plant-leafy', kind: 'plant', model: 'potted_plant_02', label: 'Leafy plant', price: 30_000, comfort: 2, note: 'Big glossy leaves in a terracotta pot.' },
  { id: 'vines', kind: 'vine', label: 'Trailing vines', price: 18_000, comfort: 1, note: 'Pothos trailing along the top of a wall.' },
  { id: 'hanger', kind: 'hanger', label: 'Hanging planter', price: 25_000, comfort: 1, note: 'A macramé hanger, the plant spilling over.' },
  { id: 'fairylights', kind: 'lights', label: 'Fairy lights', price: 12_000, comfort: 1, note: 'Warm little bulbs swagged along a wall.' },
  { id: 'lamp-sunset', kind: 'lamp', model: 'sunset', label: 'Sunset lamp', price: 38_000, comfort: 2, note: 'Throws a low orange-and-pink sun on the wall behind it, all day long.' },
  { id: 'lamp-arc', kind: 'lamp', model: 'arc', label: 'Arc floor lamp', price: 95_000, comfort: 3, note: 'A brass arc over the sofa, a warm pool of light under its dome.' },
  { id: 'lamp-linen', kind: 'lamp', model: 'linen', label: 'Linen floor lamp', price: 48_000, comfort: 2, note: 'A tall column of soft linen, glowing from the inside.' },
  { id: 'lamp-moon', kind: 'lamp', model: 'moon', label: 'Moon lamp', price: 30_000, comfort: 2, note: 'A pale glowing moon on a little wooden stand.' },
  { id: 'lamp-salt', kind: 'lamp', model: 'salt', label: 'Salt lamp', price: 16_000, comfort: 1, note: 'A rough rock of pink salt, amber from within.' },
  { id: 'lamp-neon', kind: 'lamp', model: 'neon', label: 'Neon ring', price: 42_000, comfort: 2, note: 'A ring of pink neon on the wall, its glow on the paint round it.' },
  { id: 'ledstrip', kind: 'led', label: 'LED strip', price: 22_000, comfort: 1, note: 'Round the top of the room, slowly changing colour.' },
];
export const decorById = (id: string) => DECOR.find((d) => d.id === id);
export const decorKey = (id: string) => `decor:${id}`;
export const ownsDecor = (s: LifeState, id: string) => s.owned.includes(decorKey(id));
/** what's on your walls and floor now: everything you own and haven't taken down */
export function decorUp(s: LifeState): string[] {
  const down = new Set((s.homeStyle as { roomDown?: string[] } | undefined)?.roomDown ?? []);
  return s.owned.filter((k) => k.startsWith('decor:')).map((k) => k.slice(6)).filter((id) => decorById(id) && !down.has(id));
}
export function buyDecor(s: LifeState, id: string): { ok: boolean; line: string } {
  const d = decorById(id);
  if (!d) return { ok: false, line: 'Not in the shop any more.' };
  if (ownsDecor(s, id)) return { ok: false, line: 'You already have it.' };
  return jumboOrder(s, decorKey(id), d.label, d.price);
}
/** take it down (it's kept: put it up again any time), or put it back up */
export function setDecorUp(s: LifeState, id: string, up: boolean) {
  const st = s.homeStyle as ({ roomDown?: string[] } | undefined);
  if (!st || !ownsDecor(s, id)) return;
  const down = new Set(st.roomDown ?? []);
  if (up) down.delete(id); else down.add(id);
  st.roomDown = [...down];
}
/** what it all adds to how home feels (a room full of things doesn't keep getting cosier) */
export function decorComfort(s: LifeState): number {
  return Math.min(12, decorUp(s).reduce((c, id) => c + (decorById(id)?.comfort ?? 0), 0));
}
